/**
 * Copyright ©2026 Drivepoint
 */

import {useEffect, useMemo, useState} from "react";
import {LinearProgress, Typography} from "@mui/material";
import {EventBus} from "@bainbridge-growth/node-frontend";
import {Maybe} from "../../types/Maybe";
import "./DPProgressIndicator.css";

type ProgressIndicatorProps = {
  eventNames: string[];
};

export type ProgressIndicatorState = {
  active: boolean;
  current: number;
  total: number;
  message?: string;
};

export default function DPProgressIndicator({eventNames}: ProgressIndicatorProps) {
  const [progress, setProgress] = useState<Maybe<ProgressIndicatorState>>(null);
  const percent = useMemo<number>(() => {
    if (!progress || !progress.active) return 0;
    return progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
  }, [progress]);

  function onEvent(event: any): void {
    setProgress(event.value);
  }

  useEffect(() => {
    const registrations = EventBus.registerMany(...eventNames, onEvent);
    return () => {
      EventBus.unregister(...registrations);
    };
  }, []);

  if (!progress || !progress.active) return null;

  return (
    <div className="dp-progress-indicator">
      <Typography variant="body2" className="dp-progress-indicator-message">
        {progress.message || `Processing ${progress.current} of ${progress.total}...`}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        className="dp-progress-indicator-bar"
      />
    </div>
  );
}
